##### Enums #####
enum MeasurementUnit {
  INVALID_MEASUREMENT_UNIT
  METRIC
  IMPERIAL
}

##### Core Types #####

type BoolDetailsOption {
  is_applicable: Boolean
  additional_details: String
}

type ConditionDetails {
  date_of_diagnosis: String
  current_or_planned_treatments: Boolean
  additional_details: String
}

type ConditionItem {
  has_condition: Boolean
  condition_details: ConditionDetails
}

type ProductConsumption {
  is_applicable: Boolean
  daily_consumption: Int
}

##### Conditions #####

type HighBloodPressureCondition {
  has_condition: Boolean
  condition_details: ConditionDetails
  systolic: Int
  diastolic: Int
}

type DiabetesCondition {
  has_condition: Boolean
  condition_details: ConditionDetails
  latest_hba1c_level: Int
}

type MentalHealthCondition {
  has_condition: Boolean
  condition_details: MentalHealthConditionDetails
}

type MentalHealthConditionDetails {
  date_of_diagnosis: String
  current_or_planned_treatments: Boolean
  additional_details: String
  time_taken_off: Boolean
  prescribed_medication: Boolean
}

type MusculoskeletalCondition {
  has_condition: Boolean
  condition_details: MentalHealthConditionDetails
  had_physiotherapy_surgery_injections: Boolean
}

##### Pages #####

type ClientHealthInfo {
  ##### Medical History #####

  medical_appointment_in_last_twelve_months: BoolDetailsOption
  planned_medical_appointments_or_test_results: BoolDetailsOption
  current_medication_intake: BoolDetailsOption
  client_has_no_condition: Boolean
  high_blood_pressure: HighBloodPressureCondition
  heart_disease: ConditionItem
  heart_attack: ConditionItem
  heart_valve_replacement: ConditionItem
  diabetes: DiabetesCondition
  stroke: ConditionItem
  mini_stroke: ConditionItem
  cancer_diagnosis: ConditionItem
  cancer_requiring_surgery: ConditionItem
  parkinson: ConditionItem
  dementia: ConditionItem
  multiple_sclerosis: ConditionItem
  chronic_kidney_failure: ConditionItem
  chronic_respiratory_disease: ConditionItem
  organ_transplant: ConditionItem
  cirrhosis: ConditionItem
  motor_neurone_disease: ConditionItem
  peripheral_vascular_disease: ConditionItem
  hepatitis_c: ConditionItem
  hiv: ConditionItem
  depression: MentalHealthCondition
  anxiety_or_panic: MentalHealthCondition
  bipolar_psychosis_schizophrenia: MentalHealthCondition
  other_mental_health_condition: MentalHealthCondition
  muscle_bone_joint_condition: MusculoskeletalCondition
  other_medical_condition: BoolDetailsOption
  early_retirement_due_to_ill_health: BoolDetailsOption
  medical_history_additional_details: String

  ##### Family Medical History #####

  client_has_no_family_medical_history: Boolean
  family_heart_attack: BoolDetailsOption
  family_cardiomyopathy: BoolDetailsOption
  family_diabetes: BoolDetailsOption
  family_cancer: BoolDetailsOption
  family_muscular_dystrophy: BoolDetailsOption
  family_neurological_disorder: BoolDetailsOption
  family_kidney_disease: BoolDetailsOption
  family_other_family_medical_condition: BoolDetailsOption
  family_medical_history_do_not_know: Boolean
  family_medical_history_additional_details: String

  ##### GP Information #####

  gp_registration_status: Boolean
  gp_has_access_to_english_medical_records: BoolDetailsOption
  gp_name: String
  gp_phone_number: String
  gp_address: LutherAddress
  gp_additional_notes: String

  ##### Lifestyle #####

  alcohol_consumption_units_per_week: Int
  alcohol_consumption_gp_referral: BoolDetailsOption
  has_smoked_in_the_past: Boolean
  stopped_smoking_date: String
  cigarettes_consumption: ProductConsumption
  vape_consumption: ProductConsumption
  rolling_tobacco_consumption: ProductConsumption
  other_nicotine_consumption_details: BoolDetailsOption
  recreational_drug_use: BoolDetailsOption
  motorbike_for_transport: BoolDetailsOption
  hazardous_sports_or_hobbies: BoolDetailsOption
  receives_payment_for_sport_participation: BoolDetailsOption
}

########## Inputs ##########

##### Core Types #####

input BoolDetailsOptionInput {
  is_applicable: Boolean
  additional_details: String
}

input ConditionDetailsInput {
  date_of_diagnosis: String
  current_or_planned_treatments: Boolean
  additional_details: String
}

input ConditionItemInput {
  has_condition: Boolean
  condition_details: ConditionDetailsInput
}

input ProductConsumptionInput {
  is_applicable: Boolean
  daily_consumption: Int
}

##### Conditions #####

input HighBloodPressureConditionInput {
  has_condition: Boolean
  condition_details: ConditionDetailsInput
  systolic: Int
  diastolic: Int
}

input DiabetesConditionInput {
  has_condition: Boolean
  condition_details: ConditionDetailsInput
  latest_hba1c_level: Int
}

input MentalHealthConditionInput {
  has_condition: Boolean
  condition_details: MentalHealthConditionDetailsInput
}

input MentalHealthConditionDetailsInput {
  date_of_diagnosis: String
  current_or_planned_treatments: Boolean
  additional_details: String
  time_taken_off: Boolean
  prescribed_medication: Boolean
}

input MusculoskeletalConditionInput {
  has_condition: Boolean
  condition_details: MentalHealthConditionDetailsInput
  had_physiotherapy_surgery_injections: Boolean
}

##### Pages #####

input ClientHealthInfoInput {
  ##### Medical History #####

  medical_appointment_in_last_twelve_months: BoolDetailsOptionInput
  planned_medical_appointments_or_test_results: BoolDetailsOptionInput
  current_medication_intake: BoolDetailsOptionInput
  client_has_no_condition: Boolean
  high_blood_pressure: HighBloodPressureConditionInput
  heart_disease: ConditionItemInput
  heart_attack: ConditionItemInput
  heart_valve_replacement: ConditionItemInput
  diabetes: DiabetesConditionInput
  stroke: ConditionItemInput
  mini_stroke: ConditionItemInput
  cancer_diagnosis: ConditionItemInput
  cancer_requiring_surgery: ConditionItemInput
  parkinson: ConditionItemInput
  dementia: ConditionItemInput
  multiple_sclerosis: ConditionItemInput
  chronic_kidney_failure: ConditionItemInput
  chronic_respiratory_disease: ConditionItemInput
  organ_transplant: ConditionItemInput
  cirrhosis: ConditionItemInput
  motor_neurone_disease: ConditionItemInput
  peripheral_vascular_disease: ConditionItemInput
  hepatitis_c: ConditionItemInput
  hiv: ConditionItemInput
  depression: MentalHealthConditionInput
  anxiety_or_panic: MentalHealthConditionInput
  bipolar_psychosis_schizophrenia: MentalHealthConditionInput
  other_mental_health_condition: MentalHealthConditionInput
  muscle_bone_joint_condition: MusculoskeletalConditionInput
  other_medical_condition: BoolDetailsOptionInput
  early_retirement_due_to_ill_health: BoolDetailsOptionInput
  medical_history_additional_details: String

  ##### Family Medical History #####

  client_has_no_family_medical_history: Boolean
  family_heart_attack: BoolDetailsOptionInput
  family_cardiomyopathy: BoolDetailsOptionInput
  family_diabetes: BoolDetailsOptionInput
  family_cancer: BoolDetailsOptionInput
  family_muscular_dystrophy: BoolDetailsOptionInput
  family_neurological_disorder: BoolDetailsOptionInput
  family_kidney_disease: BoolDetailsOptionInput
  family_other_family_medical_condition: BoolDetailsOptionInput
  family_medical_history_do_not_know: Boolean
  family_medical_history_additional_details: String

  ##### GP Information #####

  gp_registration_status: Boolean
  gp_has_access_to_english_medical_records: BoolDetailsOptionInput
  gp_name: String
  gp_phone_number: String
  gp_address: LutherAddressInput
  gp_additional_notes: String

  ##### Lifestyle #####

  alcohol_consumption_units_per_week: Int
  alcohol_consumption_gp_referral: BoolDetailsOptionInput
  has_smoked_in_the_past: Boolean
  stopped_smoking_date: String
  cigarettes_consumption: ProductConsumptionInput
  vape_consumption: ProductConsumptionInput
  rolling_tobacco_consumption: ProductConsumptionInput
  other_nicotine_consumption_details: BoolDetailsOptionInput
  recreational_drug_use: BoolDetailsOptionInput
  motorbike_for_transport: BoolDetailsOptionInput
  hazardous_sports_or_hobbies: BoolDetailsOptionInput
  receives_payment_for_sport_participation: BoolDetailsOptionInput
}
